import React, { useContext, useEffect, useState } from "react";
import { Row, Col, Card, Typography, Collapse } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import ClinicalCard from "./ClinicalCard";
import Graphs from "./Graphs/Graphs";
import ProgressData from "./ProgressData";
import { getPatientInfoApi } from "../../../../services/patient.services";
import useQuery from "../../../../hooks/useQuery";
import SummaryMedicines from "./SummaryMedicines";
import SummaryInvestigations from "./SummaryInvestigations";

import "./Landing.css";
import SpecialAreas from "./SpecialAreas";
import LoadingIndicator from "../../../Loaders/LoadingIndicator";
import useIsMobile from "../../../../hooks/useIsMobile";
import { UserContext } from "../../../../context/UserContext";

const Landing = () => {
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const params = useQuery();
  const isMobile = useIsMobile();
  const { userDetails } = useContext(UserContext);
  const role = userDetails?.currRole?.toLowerCase()?.trim() || "";
  const isSummaryMobile =
    isMobile && (role.includes("doctor") || role.includes("nurse"));

  const fetchPatientData = (showLoading = false) => {
    if (showLoading) setLoading(true);
    getPatientInfoApi(params?.patient)
      .then((res) => {
        setPatientData(res?.data);
        if (showLoading) setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        if (showLoading) setLoading(false);
      });
  };

  useEffect(() => {
    fetchPatientData(true);
  }, [params?.patient]);

  const hasSpecialAreas = patientData?.data?.care_items?.some(
    (item) =>
      item?.catheter ||
      item?.central_line ||
      item?.feeding_tube ||
      item?.pressure_sore_care
  );

  const specialAreasContent = hasSpecialAreas ? (
    <SpecialAreas data={patientData?.data?.care_items} />
  ) : (
    <Card
      className="special-areas-card"
      title={<Typography style={{ fontSize: "16px" }}>Special Areas</Typography>}
      style={{ marginTop: "10px" }}
      styles={{
        header: { borderBottom: "none" },
        body: { paddingTop: "0px" },
      }}
    >
      <div className="landing-summary-empty">No Special Care Areas Added</div>
    </Card>
  );

  if (loading) {
    return <LoadingIndicator message={"Loading data, please wait..."} />;
  }

  if (isSummaryMobile) {
    const data = patientData?.data;
    const items = [];

    if (data) {
      items.push({
        key: "vitals",
        label: "Vitals",
        children: <Graphs data={data} />,
      });
    }

    if (data?.clinical_diagnosis) {
      items.push({
        key: "clinical",
        label: "Clinical Diagnosis",
        children: <ClinicalCard data={data.clinical_diagnosis} />,
      });
    }

    if (data?.medicines?.[0]) {
      items.push({
        key: "summary-med",
        label: "Summary Medicines",
        children: (
          <SummaryMedicines
            data={data.medicines}
            refreshPage={() => fetchPatientData(false)}
          />
        ),
      });
    }

    items.push({
      key: "special-areas",
      label: "Special Areas",
      children: specialAreasContent,
    });

    if (data?.progress_notes) {
      items.push({
        key: "progress-notes",
        label: "Progress Notes",
        children: <ProgressData />,
      });
    }

    if (data?.investigations?.[0]) {
      items.push({
        key: "investigations",
        label: "Recent Investigations",
        children: <SummaryInvestigations data={data.investigations} />,
      });
    }

    return (
      <div className="landing-page-wrapper landing-page-wrapper--doctor-mobile">
        <Collapse
          bordered={false}
          className="landing-mobile-collapse"
          defaultActiveKey={["vitals"]}
          expandIconPosition="end"
          expandIcon={({ isActive }) => (
            <span
              className={`landing-mobile-collapse-icon${
                isActive ? " landing-mobile-collapse-icon--active" : ""
              }`}
            >
              <CaretRightOutlined />
            </span>
          )}
          items={items}
        />
      </div>
    );
  }

  return (
    <div className="landing-page-wrapper">
      <Row gutter={[8, 2]}>
        <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
          {patientData?.data?.clinical_diagnosis && (
            <ClinicalCard data={patientData?.data?.clinical_diagnosis} />
          )}
          {patientData?.data?.medicines?.[0] && (
            <SummaryMedicines
              data={patientData?.data?.medicines}
              refreshPage={() => fetchPatientData(false)}
            />
          )}
          {patientData?.data?.progress_notes && <ProgressData />}
          {patientData?.data?.investigations?.[0] && (
            <SummaryInvestigations data={patientData?.data?.investigations} />
          )}
        </Col>

        <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
          {patientData?.data && <Graphs data={patientData?.data} />}
          {specialAreasContent}
        </Col>
      </Row>
    </div>
  );
};

export default Landing;
